import { DialogComponent } from "@theme/dialog";
import { CartAddEvent } from "@theme/events";

const instantCartOpenFetchWrappedKey = Symbol.for(
  "bp.instantCartOpenFetchWrapped",
);
const CART_ADD_LOADING_HIDE_DELAY = 0;
const CART_DOM_IDLE_DELAY = 0;
const CART_ADD_LINE_POLL_MS = 50;
const CART_ADD_LOADING_MAX_MS = 15000;
const CART_REQUEST_TRACK_TIMEOUT = 15000;
const CART_ADD_REVEAL_MAX_MS = 6000;
const CART_ADD_FAILED_REVEAL_MS = 250;
const CART_SKELETON_IMAGE_SRC =
  "https://user-images.githubusercontent.com/25938785/90191444-8f443b80-ddc9-11ea-918f-da77070549b8.gif";
const kachingRefreshCartWrappedKey = Symbol.for(
  "bp.kachingRefreshCartWrapped",
);
const ATC_TRIGGER_SELECTOR = [
  'form[action*="/cart/add"] button[type="submit"]',
  'form[action*="/cart/add"] [type="submit"]',
  'button[name="add"]',
  ".add-to-cart-button",
  ".product-form__submit",
  ".quick-add__button",
  ".cross-sell-card__atb--direct",
  ".recommend-add-btn",
  ".js-micro-upsell-atc",
  ".kaching-product-upsell-item__add-to-cart",
  // Hybrid Duo offer buttons add through script, not a form.
  "button[data-hd-duo-cta]",
  // Nothing picked = aria-disabled: no add happens, so no drawer or skeleton.
  'button[data-hd-indiv-cta]:not([aria-disabled="true"])',
].join(",");
const CART_MUTATION_IGNORE_SELECTOR = [
  ".bp-cart-add-skeleton",
  ".skeletonMine",
  ".checkout-pulse-bubble",
  ".checkout-pulse-bubble__indicator",
  ".checkout-pulse-bubble__count",
  ".checkout-counter",
].join(",");

function markCartActivity() {
  window.__bpCartLastActivityAt = performance.now();
}

function getCartIdleTime() {
  return performance.now() - (window.__bpCartLastActivityAt || 0);
}

function isAutoCartMutationActive() {
  return (window.__bpAutoCartMutationDepth || 0) > 0;
}

function isCartAddSkeletonSuppressed() {
  return Boolean(window.__bpCartAddSkeletonSuppressedForSession);
}

function getMutationElement(node) {
  if (node instanceof Element) return node;
  return node?.parentElement || null;
}

function isIgnoredCartMutationNode(node) {
  const element = getMutationElement(node);
  return Boolean(element?.closest(CART_MUTATION_IGNORE_SELECTOR));
}

function getMutationText(nodes) {
  return nodes
    .filter((node) => !isIgnoredCartMutationNode(node))
    .map((node) => node.textContent?.replace(/\s+/g, " ").trim() || "")
    .filter(Boolean)
    .join("|");
}

function isMeaningfulCartMutation(mutation) {
  if (mutation.type === "attributes") return false;
  if (isIgnoredCartMutationNode(mutation.target)) return false;

  const addedNodes = [...mutation.addedNodes];
  const removedNodes = [...mutation.removedNodes];
  const changedNodes = [...addedNodes, ...removedNodes];
  if (changedNodes.length === 0) return true;

  const addedText = getMutationText(addedNodes);
  const removedText = getMutationText(removedNodes);
  if (addedText && addedText === removedText) return false;

  return changedNodes.some((node) => !isIgnoredCartMutationNode(node));
}

function installCartActivityObserver() {
  if (typeof MutationObserver === "undefined") return;

  const target =
    document.querySelector("kaching-cart-drawer") ||
    document.querySelector("cart-drawer-component");
  if (!target || window.__bpCartActivityObserverTarget === target) return;

  window.__bpCartActivityObserver?.disconnect();
  window.__bpCartActivityObserverTarget = target;
  window.__bpCartActivityObserver = new MutationObserver((mutations) => {
    syncKachingNativeSkeletonHidden();
    if (!document.body.classList.contains("bp-cart-add-loading")) return;
    if (finishCartAddLoadingIfNewLinesShown()) return;
    if (cartDrawerReadyToReveal() && getCartAddRequestsInFlight() === 0) {
      finishCartAddLoading();
      return;
    }
    if (isAutoCartMutationActive()) return;
    // Drawer repaint churn after cart/add should not reset the idle timer.
    if (
      getCartAddRequestsInFlight() === 0 &&
      getCartRequestsInFlight() === 0 &&
      getPostRequestsInFlight() === 0 &&
      getCartRenderWorkInFlight() === 0
    ) {
      return;
    }
    if (!mutations.some(isMeaningfulCartMutation)) return;

    markCartActivity();
  });

  window.__bpCartActivityObserver.observe(target, {
    characterData: true,
    childList: true,
    subtree: true,
  });
}

function ensureKachingCartSkeleton() {
  const kachingDrawer = document.querySelector("kaching-cart-drawer");
  if (!kachingDrawer) return;

  let skeleton = kachingDrawer.querySelector(":scope > .bp-cart-add-skeleton");
  const header = kachingDrawer.querySelector(":scope > .kaching-cart__header");
  const headerHeight = header instanceof HTMLElement ? header.offsetHeight : 0;

  if (headerHeight > 0) {
    kachingDrawer.style.setProperty(
      "--bp-kaching-cart-header-height",
      `${headerHeight}px`,
    );
  }

  if (skeleton) {
    if (header && header.nextElementSibling !== skeleton) {
      header.insertAdjacentElement("afterend", skeleton);
    }
    return;
  }

  const existingSkeleton = document.querySelector(
    "cart-drawer-component .skeletonMine",
  );
  skeleton = existingSkeleton
    ? existingSkeleton.cloneNode(true)
    : document.createElement("div");

  skeleton.classList.add("skeletonMine", "bp-cart-add-skeleton", "hide");
  skeleton.setAttribute("aria-hidden", "true");

  if (!skeleton.querySelector("img")) {
    const image = document.createElement("img");
    image.src = CART_SKELETON_IMAGE_SRC;
    image.alt = "";
    skeleton.appendChild(image);
  }

  if (header) {
    header.insertAdjacentElement("afterend", skeleton);
  } else {
    kachingDrawer.prepend(skeleton);
  }
}

function installKachingCartSkeletonObserver() {
  if (window.__bpKachingCartSkeletonObserver) return;

  const kachingDrawer = document.querySelector("kaching-cart-drawer");
  if (!kachingDrawer || typeof MutationObserver === "undefined") return;

  window.__bpKachingCartSkeletonObserver = new MutationObserver(() => {
    syncKachingNativeSkeletonHidden();
    if (!document.body.classList.contains("bp-cart-add-loading")) return;
    if (isAutoCartMutationActive() || isCartAddSkeletonSuppressed()) {
      kachingDrawer
        .querySelector(":scope > .bp-cart-add-skeleton")
        ?.classList.add("hide");
      return;
    }

    ensureKachingCartSkeleton();
    kachingDrawer
      .querySelector(":scope > .bp-cart-add-skeleton")
      ?.classList.remove("hide");
  });

  window.__bpKachingCartSkeletonObserver.observe(kachingDrawer, {
    childList: true,
    subtree: true,
  });
}

function syncKachingNativeSkeletonHidden() {
  document
    .querySelectorAll("kaching-cart-drawer .kaching-cart-item-skeleton")
    .forEach((el) => {
      el.hidden = true;
      el.setAttribute("aria-hidden", "true");
    });
}

function setCartSkeletonLoading(isLoading) {
  if (isLoading && isCartAddSkeletonSuppressed()) {
    isLoading = false;
  }

  syncKachingNativeSkeletonHidden();

  if (isLoading) {
    ensureKachingCartSkeleton();
    installKachingCartSkeletonObserver();
  }

  document.querySelectorAll(".skeletonMine").forEach((element) => {
    element.classList.toggle("hide", !isLoading);
  });
}

function usesKachingCartDrawer() {
  return Boolean(document.querySelector("kaching-cart-drawer"));
}

function setCartAddLoading(isLoading) {
  if (isLoading) {
    document.body.classList.add("bp-cart-add-loading");
    if (!usesKachingCartDrawer()) {
      document.body.classList.add("cartLoaderActive");
    }
  } else {
    document.body.classList.remove("bp-cart-add-loading");
    document.body.classList.remove("bp-cart-add-skeleton-suppressed");
    window.__bpCartAddSkeletonSuppressedForSession = false;
    if (!window.__bpCartAddLoaderHadCartLoaderActive) {
      document.body.classList.remove("cartLoaderActive");
    }
  }

  document.querySelectorAll(".cart-drawer__content").forEach((element) => {
    if (isLoading) {
      element.classList.add("keep-loading");
    } else if (element.dataset.bpCartAddHadKeepLoading !== "true") {
      element.classList.remove("keep-loading");
    }
  });

  setCartSkeletonLoading(isLoading);
}

function suppressCartAddSkeletonForSession() {
  if (!window.__bpCartAddLoaderSessionActive) return;

  window.__bpCartAddSkeletonSuppressedForSession = true;
  document.body.classList.add("bp-cart-add-skeleton-suppressed");
  setCartSkeletonLoading(false);
}

function startCartAddLoading() {
  markCartActivity();
  installCartActivityObserver();
  installKachingCartApiHook();

  if (window.__bpCartAddLoadingStopTimeout) {
    window.clearTimeout(window.__bpCartAddLoadingStopTimeout);
    window.__bpCartAddLoadingStopTimeout = undefined;
    window.__bpCartAddLoadingStopPending = false;
  }

  if (!window.__bpCartAddLoaderSessionActive) {
    window.__bpCartAddLoaderSessionActive = true;
    window.__bpCartAddLoaderStartedAt = Date.now();
    window.__bpCartAddBaselineLines = countCartDrawerLines();
    window.__bpCartAddFailedAt = undefined;
    window.__bpCartAddLoaderHadCartLoaderActive =
      document.body.classList.contains("cartLoaderActive");
    document.querySelectorAll(".cart-drawer__content").forEach((element) => {
      element.dataset.bpCartAddHadKeepLoading = String(
        element.classList.contains("keep-loading"),
      );
    });
  }

  setCartAddLoading(true);
}

function getRequestUrl(input) {
  const url = typeof input === "string" ? input : input?.url;
  if (typeof url !== "string") return "";

  try {
    return new URL(url, window.location.origin).href;
  } catch (_error) {
    return url;
  }
}

function hasRequestResourceCompleted(token) {
  if (!token.url || !window.performance?.getEntriesByType) return false;

  return performance.getEntriesByType("resource").some((entry) => {
    return (
      entry.name === token.url &&
      entry.startTime >= token.startedAt - 20 &&
      entry.responseEnd > 0
    );
  });
}

function createRequestToken(input, stopRequest) {
  markCartActivity();

  const token = {
    startedAt: performance.now(),
    url: getRequestUrl(input),
  };

  token.timeout = window.setTimeout(() => {
    stopRequest(token);
  }, CART_REQUEST_TRACK_TIMEOUT);

  token.poll = window.setInterval(() => {
    if (hasRequestResourceCompleted(token)) {
      stopRequest(token);
    }
  }, 50);

  return token;
}

function clearRequestToken(token) {
  window.clearTimeout(token.timeout);
  window.clearInterval(token.poll);
}

function startCartAddRequest(input) {
  const token = createRequestToken(input, stopCartAddRequest);

  window.__bpCartAddRequestTokens ||= new Set();
  window.__bpCartAddRequestTokens.add(token);
  window.__bpCartAddRequestsInFlight = window.__bpCartAddRequestTokens.size;

  return token;
}

function startPostRequest(input) {
  const token = createRequestToken(input, stopPostRequest);

  window.__bpPostRequestTokens ||= new Set();
  window.__bpPostRequestTokens.add(token);
  window.__bpPostRequestsInFlight = window.__bpPostRequestTokens.size;

  return token;
}

function startCartRequest(input) {
  const token = createRequestToken(input, stopCartRequest);

  window.__bpCartRequestTokens ||= new Set();
  window.__bpCartRequestTokens.add(token);
  window.__bpCartRequestsInFlight = window.__bpCartRequestTokens.size;

  return token;
}

function startCartRenderWork() {
  markCartActivity();

  const token = {};
  window.__bpCartRenderTokens ||= new Set();
  window.__bpCartRenderTokens.add(token);
  window.__bpCartRenderWorkInFlight = window.__bpCartRenderTokens.size;

  return token;
}

function getCartAddRequestsInFlight() {
  window.__bpCartAddRequestsInFlight =
    window.__bpCartAddRequestTokens?.size || 0;
  return window.__bpCartAddRequestsInFlight;
}

function getCartRequestsInFlight() {
  window.__bpCartRequestsInFlight = window.__bpCartRequestTokens?.size || 0;
  return window.__bpCartRequestsInFlight;
}

function getCartRenderWorkInFlight() {
  window.__bpCartRenderWorkInFlight =
    window.__bpCartRenderTokens?.size || 0;
  return window.__bpCartRenderWorkInFlight;
}

function getPostRequestsInFlight() {
  window.__bpPostRequestsInFlight = window.__bpPostRequestTokens?.size || 0;
  return window.__bpPostRequestsInFlight;
}

function stopPostRequest(token) {
  if (token && window.__bpPostRequestTokens?.has(token)) {
    clearRequestToken(token);
    window.__bpPostRequestTokens.delete(token);
    markCartActivity();
  }

  if (
    window.__bpCartAddLoadingStopPending &&
    getCartAddRequestsInFlight() === 0 &&
    getCartRequestsInFlight() === 0 &&
    getCartRenderWorkInFlight() === 0 &&
    getPostRequestsInFlight() === 0
  ) {
    scheduleCartAddLoadingStop();
  }
}

function stopCartAddRequest(token) {
  if (token && window.__bpCartAddRequestTokens?.has(token)) {
    clearRequestToken(token);
    window.__bpCartAddRequestTokens.delete(token);
    markCartActivity();
  }

  if (getCartAddRequestsInFlight() === 0 && !finishCartAddLoadingIfNewLinesShown()) {
    scheduleCartAddLoadingStop();
  }
}

function stopCartRequest(token) {
  if (token && window.__bpCartRequestTokens?.has(token)) {
    clearRequestToken(token);
    window.__bpCartRequestTokens.delete(token);
    markCartActivity();
  }

  if (getCartRequestsInFlight() === 0 && !finishCartAddLoadingIfNewLinesShown()) {
    scheduleCartAddLoadingStop();
  }
}

function stopCartRenderWork(token) {
  if (token && window.__bpCartRenderTokens?.has(token)) {
    window.__bpCartRenderTokens.delete(token);
    markCartActivity();
  }

  if (getCartRenderWorkInFlight() === 0) {
    scheduleCartAddLoadingStop();
  }
}

function getCartAddLoadingStopDelay() {
  if (
    getCartAddRequestsInFlight() > 0 ||
    getCartRequestsInFlight() > 0 ||
    getCartRenderWorkInFlight() > 0 ||
    getPostRequestsInFlight() > 0
  ) {
    return null;
  }

  const remainingIdleTime = CART_DOM_IDLE_DELAY - getCartIdleTime();
  return Math.max(0, remainingIdleTime);
}

function scheduleCartAddLoadingStop(delay = CART_ADD_LOADING_HIDE_DELAY) {
  if (!window.__bpCartAddLoaderSessionActive) return;

  if (window.__bpCartAddLoadingStopTimeout) {
    window.clearTimeout(window.__bpCartAddLoadingStopTimeout);
  }

  window.__bpCartAddLoadingStopPending = true;
  window.__bpCartAddLoadingStopTimeout = window.setTimeout(() => {
    window.__bpCartAddLoadingStopTimeout = undefined;

    const nextDelay = getCartAddLoadingStopDelay();
    if (nextDelay === null) {
      return;
    }

    if (nextDelay > 0) {
      scheduleCartAddLoadingStop(nextDelay);
      return;
    }

    finishCartAddLoading();
  }, delay);
}

function countCartDrawerLines() {
  const kachingDrawer = document.querySelector("kaching-cart-drawer");
  if (kachingDrawer) {
    return kachingDrawer.querySelectorAll(
      ".kaching-cart-item:not(.bp-auto-gift-loader__item):not(.kaching-cart-item-skeleton)",
    ).length;
  }
  return document.querySelectorAll("cart-drawer-component .cart-items__table-row")
    .length;
}

function kachingCartLineCount() {
  try {
    const items = window.kachingCartApi?.getCartData()?.items;
    if (!Array.isArray(items)) return 0;
    return items.filter((item) => item.handle !== "kaching-cart-toggle").length;
  } catch (_error) {
    return 0;
  }
}

function getCartAddSessionAgeMs() {
  if (!window.__bpCartAddLoaderStartedAt) return 0;
  return Date.now() - window.__bpCartAddLoaderStartedAt;
}

// Do not drop the skeleton until real rows are painted (or we hit the safety cap).
function cartDrawerReadyToReveal() {
  const baseline = window.__bpCartAddBaselineLines || 0;
  const lines = countCartDrawerLines();
  if (lines > baseline) return true;

  // An add that failed brings no new line: stop waiting for one once nothing else is
  // being added (the short pause lets a paid-only retry start first).
  if (
    window.__bpCartAddFailedAt &&
    getCartAddRequestsInFlight() === 0 &&
    Date.now() - window.__bpCartAddFailedAt >= CART_ADD_FAILED_REVEAL_MS
  ) {
    return true;
  }

  if (usesKachingCartDrawer()) {
    const apiLines = kachingCartLineCount();
    if (apiLines > baseline && lines <= baseline) return false;
    if (apiLines === 0 && lines === 0 && baseline === 0) return false;
  } else if (baseline === 0 && lines === 0) {
    return false;
  }

  if (baseline > 0 && lines >= baseline && getCartAddRequestsInFlight() === 0) {
    return true;
  }

  return getCartAddSessionAgeMs() >= CART_ADD_LOADING_MAX_MS;
}

function finishCartAddLoading() {
  if (!cartDrawerReadyToReveal()) {
    scheduleCartAddLoadingStop(CART_ADD_LINE_POLL_MS);
    return;
  }
  if (window.__bpCartAddLoadingStopTimeout) {
    window.clearTimeout(window.__bpCartAddLoadingStopTimeout);
    window.__bpCartAddLoadingStopTimeout = undefined;
  }

  window.__bpCartAddLoadingStopPending = false;
  setCartAddLoading(false);
  document.querySelectorAll(".cart-drawer__content").forEach((element) => {
    delete element.dataset.bpCartAddHadKeepLoading;
  });
  window.__bpCartAddLoaderHadCartLoaderActive = false;
  window.__bpCartAddLoaderSessionActive = false;
  window.__bpCartAddLoaderStartedAt = undefined;
  keepRevealedLinesOpaque();
}

// Right after the skeleton goes, Kaching fades every line while it adds its automatic
// extras (the warranty toggle). Keep the fresh lines at full opacity until it settles,
// so later edits by the shopper still get Kaching's normal fade.
function keepRevealedLinesOpaque() {
  const body = document.body;
  window.clearInterval(window.__bpCartAddRevealTimer);
  if (!document.querySelector("kaching-cart-drawer")) return;

  body.classList.add("bp-cart-add-revealed");
  const startedAt = Date.now();
  let sawBusy = false;
  let idleChecks = 0;
  window.__bpCartAddRevealTimer = window.setInterval(() => {
    const busy = Boolean(
      document.querySelector("kaching-cart-drawer .kaching-cart__disabled"),
    );
    if (busy) {
      sawBusy = true;
      idleChecks = 0;
    } else if (sawBusy) {
      idleChecks += 1;
    }
    if (idleChecks >= 3 || Date.now() - startedAt > CART_ADD_REVEAL_MAX_MS) {
      window.clearInterval(window.__bpCartAddRevealTimer);
      body.classList.remove("bp-cart-add-revealed");
    }
  }, 150);
}

// Reveal the drawer once it shows more lines than when the click started and no cart
// add or read is still running. A click that only bumps a quantity adds no line, so it
// falls through to the idle timer.
function finishCartAddLoadingIfNewLinesShown() {
  if (!window.__bpCartAddLoaderSessionActive) return false;
  if (getCartAddRequestsInFlight() > 0) return false;
  const lines = countCartDrawerLines();
  if (lines <= (window.__bpCartAddBaselineLines || 0)) return false;

  finishCartAddLoading();
  return true;
}

function getFetchMethod(input, init) {
  return (
    init?.method ||
    (typeof Request !== "undefined" && input instanceof Request
      ? input.method
      : "") ||
    "GET"
  ).toUpperCase();
}

function isPostRequest(input, init) {
  return getFetchMethod(input, init) === "POST";
}

function isCartMutationPost(input, init) {
  if (!isPostRequest(input, init) || isCartAddPost(input, init)) return false;

  const url = getRequestUrl(input);
  if (typeof url !== "string") return false;

  try {
    const pathname = new URL(url).pathname;
    return /^\/cart\/(?:change|clear|update)\.js$/.test(pathname);
  } catch (_error) {
    return /\/cart\/(?:change|clear|update)\.js/.test(url);
  }
}

function isCartAddPost(input, init) {
  const url = getRequestUrl(input);
  if (typeof url !== "string") return false;
  if (!isPostRequest(input, init)) return false;

  try {
    return /\/cart\/add(?:\.js)?$/.test(new URL(url).pathname);
  } catch (_error) {
    return /\/cart\/add(?:\.js)?(?:\?|$)/.test(url);
  }
}

function isCartRequest(input) {
  const url = getRequestUrl(input);
  if (typeof url !== "string") return false;

  try {
    const pathname = new URL(url).pathname;
    return /^\/cart(?:\/add|\/(?:add|change|clear|update)\.js|\.js)$/.test(pathname);
  } catch (_error) {
    return /\/cart(?:\/add(?:\?|$)|\/(?:add|change|clear|update)\.js|\.js)/.test(url);
  }
}

function wrapKachingRefreshCart(api) {
  if (
    !api ||
    typeof api.refreshCart !== "function" ||
    api.refreshCart[kachingRefreshCartWrappedKey]
  ) {
    return;
  }

  const originalRefreshCart = api.refreshCart;
  const wrappedRefreshCart = function (...args) {
    return Promise.resolve(originalRefreshCart.apply(this, args));
  };

  Object.defineProperty(wrappedRefreshCart, kachingRefreshCartWrappedKey, {
    value: true,
  });
  api.refreshCart = wrappedRefreshCart;
}

function installKachingCartApiHook() {
  wrapKachingRefreshCart(window.kachingCartApi);

  if (window.__bpKachingCartApiHookRefreshScheduled) return;

  window.__bpKachingCartApiHookRefreshScheduled = true;
  let attempts = 0;
  const refreshInterval = window.setInterval(() => {
    wrapKachingRefreshCart(window.kachingCartApi);
    attempts += 1;

    if (
      attempts >= 120 ||
      window.kachingCartApi?.refreshCart?.[kachingRefreshCartWrappedKey]
    ) {
      window.clearInterval(refreshInterval);
    }
  }, 250);
}

function openCartDrawerInstantly() {
  const kachingDrawer = document.querySelector("kaching-cart-drawer");

  if (kachingDrawer?.getAttribute("open") === "true") return;

  if (
    window.kachingCartApi &&
    typeof window.kachingCartApi.openCart === "function"
  ) {
    window.kachingCartApi.openCart();
    return;
  }

  const cartDrawer = document.querySelector("cart-drawer-component");
  const dialog = cartDrawer?.querySelector("dialog");

  if (dialog?.open) return;

  if (cartDrawer && typeof cartDrawer.open === "function") {
    cartDrawer.open();
    return;
  }

  document.dispatchEvent(
    new CartAddEvent({}, "cart-add-fetch", {
      source: "cart-add-fetch",
    }),
  );
}

function isDisabledAtcTrigger(trigger) {
  return (
    trigger.hasAttribute("disabled") ||
    trigger.getAttribute("aria-disabled") === "true" ||
    trigger.closest("[disabled], [aria-disabled='true']")
  );
}

function isAtcTriggerAddAction(trigger) {
  const crossSellToggle = trigger.closest("[data-cs-toggle-popup]");
  if (!crossSellToggle) return true;

  const cardId = crossSellToggle.getAttribute("data-cs-toggle-popup");
  const popup = cardId ? document.getElementById(`popup-${cardId}`) : null;
  return Boolean(popup?.classList.contains("is-open"));
}

function shouldSkipAtcLoaderForTrigger(trigger) {
  // Direct-checkout bundles add their full payload before navigating, without a drawer.
  if (
    trigger.matches('[data-bundle-submit], [data-one-time-add]') &&
    trigger.closest('[data-subscription-bundle][data-coc-direct-checkout="true"]')
  ) return true;

  return Boolean(
    (trigger.closest(".kaching-product-upsell-item") &&
      trigger.closest(".kaching-product-upsell-item__add-to-cart")) ||
      (trigger.closest("[data-micro-upsell]") &&
        trigger.closest(".js-micro-upsell-atc")) ||
      (trigger.closest(".cart-drawer-triggered-upsell") &&
        trigger.closest(".recommend-add-btn")),
  );
}

function handleAtcTriggerClick(event) {
  const trigger =
    event.target instanceof Element
      ? event.target.closest(ATC_TRIGGER_SELECTOR)
      : null;

  if (!trigger || isDisabledAtcTrigger(trigger)) return;
  if (shouldSkipAtcLoaderForTrigger(trigger)) return;
  if (!isAtcTriggerAddAction(trigger)) return;

  openCartDrawerInstantly();
  startCartAddLoading();
  scheduleCartAddLoadingStop();
}

function installKachingSkeletonHider() {
  if (window.__bpKachingSkeletonHiderInstalled) return;
  window.__bpKachingSkeletonHiderInstalled = true;
  syncKachingNativeSkeletonHidden();
  if (typeof MutationObserver === "undefined") return;
  new MutationObserver(() => syncKachingNativeSkeletonHidden()).observe(
    document.documentElement,
    { childList: true, subtree: true },
  );
}

function installAtcOpenLoaderHook() {
  if (window.__bpAtcOpenLoaderHookInstalled) return;

  window.__bpAtcOpenLoaderHookInstalled = true;
  installKachingSkeletonHider();
  document.addEventListener("click", handleAtcTriggerClick, {
    capture: true,
  });
  document.addEventListener(
    "botanique:auto-cart:start",
    suppressCartAddSkeletonForSession,
  );
}

function installInstantCartOpenFetchHook() {
  const wrapFetch = (fetchFunction) => {
    if (
      typeof fetchFunction !== "function" ||
      fetchFunction[instantCartOpenFetchWrappedKey]
    ) {
      return fetchFunction;
    }

    const wrappedFetch = (input, init) => {
      const isPost = isPostRequest(input, init);
      const isCartAdd = isCartAddPost(input, init);
      const isCart = isCartRequest(input);
      const shouldTrackRequest =
        !window.__bpFetchTrackingDepth &&
        window.__bpCartAddLoaderSessionActive &&
        !isAutoCartMutationActive();
      // Third-party pixels POST on every add; only cart endpoints may hold the skeleton.
      const postToken =
        shouldTrackRequest && isPost && isCart
          ? startPostRequest(input)
          : undefined;
      const cartAddToken =
        shouldTrackRequest && isCartAdd
          ? startCartAddRequest(input)
          : undefined;
      const cartToken =
        shouldTrackRequest && isCartMutationPost(input, init)
          ? startCartRequest(input)
          : undefined;

      window.__bpFetchTrackingDepth = (window.__bpFetchTrackingDepth || 0) + 1;

      const stopTrackedRequests = () => {
        if (isPost) {
          stopPostRequest(postToken);
        }

        if (isCartAdd) {
          stopCartAddRequest(cartAddToken);
        }

        if (isCart) {
          stopCartRequest(cartToken);
        }
      };

      let fetchResult;
      try {
        fetchResult = fetchFunction.call(window, input, init);
      } catch (error) {
        window.__bpFetchTrackingDepth = Math.max(
          0,
          (window.__bpFetchTrackingDepth || 0) - 1,
        );
        stopTrackedRequests();
        throw error;
      }

      window.__bpFetchTrackingDepth = Math.max(
        0,
        (window.__bpFetchTrackingDepth || 0) - 1,
      );

      if (cartAddToken) {
        const markCartAddFailed = () => {
          if (window.__bpCartAddLoaderSessionActive) {
            window.__bpCartAddFailedAt = Date.now();
          }
        };
        Promise.resolve(fetchResult).then((response) => {
          if (response && response.ok === false) markCartAddFailed();
        }, markCartAddFailed);
      }

      return Promise.resolve(fetchResult).finally(stopTrackedRequests);
    };

    Object.defineProperty(wrappedFetch, instantCartOpenFetchWrappedKey, {
      value: true,
    });
    return wrappedFetch;
  };

  window.__bpInstantCartOpenFetchHookInstalled = true;
  const fetchDescriptor = Object.getOwnPropertyDescriptor(window, "fetch");
  let currentFetch = wrapFetch(window.fetch);

  Object.defineProperty(window, "fetch", {
    configurable: true,
    enumerable: fetchDescriptor?.enumerable ?? true,
    get() {
      return currentFetch;
    },
    set(nextFetch) {
      currentFetch = wrapFetch(nextFetch);
    },
  });
}

if (typeof window !== "undefined" && window.fetch) {
  installKachingSkeletonHider();
  installAtcOpenLoaderHook();
  installInstantCartOpenFetchHook();
  installKachingCartApiHook();

  if (!window.__bpInstantCartOpenFetchHookRefreshScheduled) {
    window.__bpInstantCartOpenFetchHookRefreshScheduled = true;
    window.addEventListener("pageshow", installInstantCartOpenFetchHook);
    window.addEventListener("load", installInstantCartOpenFetchHook);

    let attempts = 0;
    const refreshInterval = window.setInterval(() => {
      installInstantCartOpenFetchHook();
      attempts += 1;

      if (attempts >= 80) {
        window.clearInterval(refreshInterval);
      }
    }, 250);
  }
}

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute("auto-open")) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined("shopify-payment-terms").then(() => {
      const installmentsContent = document.querySelector(
        "shopify-payment-terms",
      )?.shadowRoot;
      const cta = installmentsContent?.querySelector(
        "#shopify-installments-cta",
      );
      cta?.addEventListener("click", this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get("cart-drawer-component")) {
  customElements.define("cart-drawer-component", CartDrawerComponent);
}
